import LazyLoad from 'vanilla-lazyload';
import EmblaCarousel from 'embla-carousel';
import Player from '@vimeo/player';
import utils from 'pagedesigner/util/utils';
import debounce from 'lodash/debounce';

const { getPaginationTabLabel } = require('org/util/emblaCarousel');

let vimeoPlayer = null;
let vimeoPlayerReady = false;

var lazyLoad = new LazyLoad({
    thresholds: '100px -1px 100px -1px',
    data_src: 'lazy'
});

let sliderInit = false;
let embla;
let videoObserver = null;
let zoomModalCloseTime = 0;

const emblaOptions = {
    loop: true,
    duration: 15
};

/**
 * Initialize the Vimeo player on the video slide's div element
 * @param {HTMLElement} videoSlide - the .video-slide element
 */
const initVimeoPlayer = (videoSlide) => {
    if (vimeoPlayer) return;

    const videoId = videoSlide.dataset.videoId;
    const playerDiv = videoSlide.querySelector('.pdp-video-player');
    if (!videoId || !playerDiv) return;

    vimeoPlayer = new Player(playerDiv, {
        id: videoId,
        autoplay: true,
        loop: true,
        controls: false,
        quality: '720p'
    });
    vimeoPlayer.ready().then(() => {
        vimeoPlayerReady = true;
        const productName = videoSlide.dataset.productName;
        if (productName && vimeoPlayer.element) {
            vimeoPlayer.element.setAttribute('title', productName);
            vimeoPlayer.element.setAttribute('aria-label', productName);
        }
    });
};

/**
 * Play or pause the Vimeo player
 * @param {string} action - 'play' or 'pause'
 */
const controlVimeoPlayer = (action) => {
    if (!vimeoPlayer || !vimeoPlayerReady) return;
    if (action === 'play') {
        vimeoPlayer.play().catch(function () { }); // eslint-disable-line no-empty-function
    } else if (action === 'pause') {
        vimeoPlayer.pause().catch(function () { }); // eslint-disable-line no-empty-function
    }
};

/**
 * Tear down the video IntersectionObserver
 */
const destroyVideoObserver = () => {
    if (videoObserver) {
        videoObserver.disconnect();
        videoObserver = null;
    }
};

/**
 * Setup IntersectionObserver for video slides when Embla is not initialized (desktop/grid view).
 * Plays video when visible, pauses when out of viewport.
 */
const setupVideoObserver = () => {
    destroyVideoObserver();

    var rootNode = document.querySelector('.embla-product-images');
    if (!rootNode) return;

    var videoSlides = rootNode.querySelectorAll('.video-slide');
    if (!videoSlides.length) return;

    videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
            if (entry.isIntersecting) {
                initVimeoPlayer(entry.target);
                controlVimeoPlayer('play');
            } else {
                controlVimeoPlayer('pause');
            }
        });
    }, { threshold: 0.5 });

    videoSlides.forEach(function (slide) {
        videoObserver.observe(slide);
    });
};

/**
 * Setup navigation buttons (prev/next) for Embla
 * @param {Object} emblaApi - Embla carousel instance
 * @param {HTMLElement} rootNode - The carousel wrapper element (buttons live here, outside viewport)
 */
const setupNavigation = (emblaApi, rootNode) => {
    const prevBtnNode = rootNode.querySelector('.embla-button-prev');
    const nextBtnNode = rootNode.querySelector('.embla-button-next');

    if (prevBtnNode) {
        prevBtnNode.addEventListener('click', emblaApi.scrollPrev, false);
    }
    if (nextBtnNode) {
        nextBtnNode.addEventListener('click', emblaApi.scrollNext, false);
    }
};

/**
 * Setup pagination dots for Embla
 * @param {Object} emblaApi - Embla carousel instance
 * @param {HTMLElement} rootNode - The carousel root element
 */
const setupPagination = (emblaApi, rootNode) => {
    const paginationEl = rootNode.querySelector('.embla-pagination');
    if (!paginationEl) return;

    if (!paginationEl.getAttribute('role')) {
        paginationEl.setAttribute('role', 'tablist');
    }

    const updateDots = () => {
        const dots = paginationEl.querySelectorAll('.embla-pagination-bullet');
        const selected = emblaApi.selectedScrollSnap();
        dots.forEach((dot, index) => {
            const isSelected = index === selected;
            dot.classList.toggle('embla-pagination-bullet-active', isSelected);
            dot.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });
    };

    const renderDots = () => {
        paginationEl.innerHTML = '';
        const slides = emblaApi.scrollSnapList();
        const total = slides.length;

        slides.forEach((_, index) => {
            const dot = document.createElement('button');
            dot.classList.add('embla-pagination-bullet');
            dot.type = 'button';
            dot.setAttribute('role', 'tab');
            dot.setAttribute('aria-selected', 'false');
            dot.setAttribute('aria-label', getPaginationTabLabel(index + 1, total));
            dot.addEventListener('click', () => emblaApi.scrollTo(index, true));
            paginationEl.appendChild(dot);
        });
        updateDots();
    };

    renderDots();
    emblaApi.on('select', updateDots);
    emblaApi.on('reInit', renderDots);
};

const destroyEmbla = () => {
    if (sliderInit && embla) {
        var rootNode = document.querySelector('.embla-product-images');

        embla.destroy();
        embla = null;
        sliderInit = false;

        if (rootNode) {
            rootNode.classList.remove('embla-initialized');
        }

        setupVideoObserver();
    }
};

const initEmbla = () => {
    const shouldInit = document.body.clientWidth < utils.breakpoints.md;

    if (shouldInit && !sliderInit) {
        var rootNode = document.querySelector('.embla-product-images');

        if (rootNode) {
            var viewportNode = rootNode.querySelector('.embla__viewport');
            var slides = rootNode.querySelectorAll('.embla-slide');

            // Don't initialize Embla if there's only one slide
            if (slides.length <= 1) {
                setupVideoObserver();
                return;
            }

            try {
                embla = EmblaCarousel(viewportNode, emblaOptions); // eslint-disable-line new-cap

                setupNavigation(embla, rootNode);
                setupPagination(embla, rootNode);

                const syncVideoPlayPause = () => {
                    var currentSlideIndex = embla.selectedScrollSnap();
                    var currentSlideElement = slides[currentSlideIndex];

                    if (currentSlideElement && currentSlideElement.classList.contains('video-slide')) {
                        initVimeoPlayer(currentSlideElement);
                        controlVimeoPlayer('play');
                    } else {
                        controlVimeoPlayer('pause');
                    }
                };

                embla.on('select', syncVideoPlayPause);
                embla.on('init', syncVideoPlayPause);

                rootNode.classList.add('embla-initialized');
                sliderInit = true;
                lazyLoad.update();
                setupVideoObserver();
            } catch (e) {
                console.error('Embla initialization failed:', e); // eslint-disable-line no-console
            }
        }
    } else if (shouldInit && sliderInit && embla) {
        // Orientation/resize change while on mobile: reInit to recalc dimensions
        embla.reInit();
    } else if (!shouldInit && sliderInit) {
        var zoomModal = document.getElementById('mobileZoomImageCarouselModal');
        if (zoomModal && zoomModal.classList.contains('show')) return;
        if (Date.now() - zoomModalCloseTime < 500) return;
        destroyEmbla();
    } else if (!shouldInit && !sliderInit) {
        setupVideoObserver();
    }
};

const updateSlides = ($pdpCarousel, productData, colorName) => {
    const $emblaContainer = $pdpCarousel.find('.embla-container');

    // Ensure container exists
    if ($emblaContainer.length === 0) {
        $pdpCarousel.append('<div class="embla-container pb-6 pb-md-0"></div>');
    }

    // Destroy existing instance to allow clean DOM updates
    destroyEmbla();

    // Preserve video and badge elements
    const $videoSlide = $emblaContainer.find('.video-slide');
    const $videoClone = $videoSlide.length ? $videoSlide.clone(true) : false;

    const $badgeIcon = $emblaContainer.find('[class*="icon-badge"]').first();
    const $badgeIconClone = $badgeIcon.length ? $badgeIcon.clone(true) : false;

    $emblaContainer.empty();

    const pdpImages = productData.images.pdp;

    if (pdpImages && pdpImages.length > 0) {
        pdpImages.forEach((element, i) => {
            const zoomImageUlr = (productData.imagesZoom && productData.imagesZoom.pdpZoom[i]) ? productData.imagesZoom.pdpZoom[i].url : false;
            const altCheck = element.alt ? element.alt : productData.productName;
            const altTag = colorName !== null ? `${altCheck} - ${colorName} - ${i + 1}` : altCheck;

            const slideHtml = `
                <div class="embla-slide slide ${zoomImageUlr ? 'zoom-hires' : 'zoom-disabled'}" data-index="${i}" data-zoom-url="${zoomImageUlr || ''}">
                    <img src="${element.url}" class="slide-img" alt="${altTag}" />
                </div>
            `;

            const $slide = $(slideHtml);
            if (i === 0 && $badgeIconClone) {
                $slide.prepend($badgeIconClone);
            }
            $emblaContainer.append($slide);
        });
    }

    if ($videoClone) {
        $videoClone.appendTo($emblaContainer);
    }

    // Re-add pagination and navigation if missing
    if ($pdpCarousel.find('.embla-pagination').length === 0 && pdpImages.length > 1) {
        $pdpCarousel.append('<div class="embla-pagination d-md-none"></div>');
    }
    if ($pdpCarousel.find('.embla-button-prev').length === 0 && pdpImages.length > 1) {
        const carouselResources = window.carouselResources || {};
        const prevLabel = carouselResources.prev || 'Previous';
        const nextLabel = carouselResources.next || 'Next';

        $pdpCarousel.append(
            '<button class="embla-button-prev d-md-none" type="button" aria-label="' + prevLabel + '"><span class="sr-only">' + prevLabel + '</span></button>'
            + '<button class="embla-button-next d-md-none" type="button" aria-label="' + nextLabel + '"><span class="sr-only">' + nextLabel + '</span></button>'
        );
    }

    // Update single image class on container
    $('.primary-images').toggleClass('pdp-single-image', (!$videoClone && pdpImages.length === 1));

    // Re-initialize Embla if needed
    initEmbla();
};

/**
 * Get the currently selected slide index (0-based), or -1 if not initialized
 * @returns {number} The 0-based index of the selected slide, or -1 if not initialized
 */
const getSelectedIndex = () => (sliderInit && embla ? embla.selectedScrollSnap() : -1);

/**
 * Scroll to a slide by index (0-based). No-op if not initialized or index invalid
 * @param {number} index - 0-based slide index
 * @param {boolean} [jump] - if true, skip animation (instant jump) - use when restoring after modal close
 */
const scrollToSlide = (index, jump) => {
    if (!sliderInit || !embla || typeof index !== 'number' || index < 0) return;
    const maxIndex = embla.scrollSnapList().length - 1;
    if (index <= maxIndex) {
        embla.scrollTo(index, !!jump);
    }
};

/**
 * Reinitialize Embla at a specific slide index. Use after zoom modal close (iOS Safari) to restore layout.
 * @param {number} index - 0-based slide index
 */
const reinitAtSlide = (index) => {
    if (!sliderInit || !embla || typeof index !== 'number' || index < 0) return;
    const maxIndex = embla.scrollSnapList().length - 1;
    if (index <= maxIndex) {
        embla.reInit({ startIndex: index });
    }
};

const setZoomModalCloseTime = () => {
    zoomModalCloseTime = Date.now();
};

const getEmbla = () => (sliderInit && embla ? embla : null);

export default {
    init: () => {
        // Only add listener once
        if (!window.pdpCarouselListenerAdded) {
            window.addEventListener('resize', debounce(initEmbla, 150));
            window.pdpCarouselListenerAdded = true;
        }
        initEmbla();
    },
    destroy: destroyEmbla,
    update: initEmbla,
    updateSlides: updateSlides,
    getSelectedIndex,
    scrollToSlide,
    reinitAtSlide,
    setZoomModalCloseTime,
    getEmbla
};
